import type {
  PagedModelClientPost,
  SearchPostsRequest
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { API_CACHE_KEYS } from '@/features/api/constants/cache';
import { useGrapevinePostApi } from '@/features/api/hooks/grapevine-post/useGrapevinePostApi';
import { useAuth } from '@/features/authorization/provider/useAuth';
import { flattenPostsToGenericPostDetails } from '@/features/posts/utils/flattenPostsToGenericPostDetails';

export const useSearchPost = (requestParameters: SearchPostsRequest) => {
  const { isAuthenticated } = useAuth();
  const api = useGrapevinePostApi();

  return useInfiniteQuery({
    queryKey: [API_CACHE_KEYS.POST_SEARCH, { ...requestParameters }],
    queryFn: ({ pageParam }) =>
      api.searchPosts({
        ...requestParameters,
        page: pageParam
      }),
    enabled: isAuthenticated && requestParameters.search.length >= 3,
    initialPageParam: 0,
    select: useCallback(
      (data: InfiniteData<PagedModelClientPost, number>) =>
        data.pages.flatMap((page) =>
          flattenPostsToGenericPostDetails(page.content)
        ),
      []
    ),
    getNextPageParam: (lastPage) =>
      (lastPage.page?.totalElements ?? 0) >
      ((lastPage.page?.number ?? 0) + 1) * (lastPage.page?.size ?? 10)
        ? (lastPage.page?.number ?? 0) + 1
        : undefined
  });
};
